import AdUnitIds from '@/pages/dsp/AdUnitIds.tsx'
import {Button, Form, Input, InputNumber, Radio, Select, Spin, Switch} from 'antd'
import FormItem from 'antd/es/form/FormItem'
import {DspBean} from 'common/api/api'
import {dspApi} from 'common/api/entitiesApi.tsx'
import {AnchorsForm} from 'common/components/AnchorsForm.tsx'
import {ButtonsPanel} from 'common/components/ButtonsPanel'
import {CountrySelector} from 'common/components/dictionary/CountrySelector.tsx'
import {FormCard} from 'common/components/FormCard'
import {PublisherSelector} from 'common/components/reloadable/PublisherSelector.tsx'
import {SspSelector} from 'common/components/reloadable/SspSelector.tsx'
import {StatusSwitch} from 'common/components/StatusSwitch'
import {useAppContext} from 'common/context/AppContext.tsx'
import { queueListRepositionOnModifiedChange } from 'common/util/listNavigation.ts';
import {apiCall, FormMode} from 'common/util/Util.tsx'
import React, {useEffect, useState} from 'react'
import {useNavigate, useParams} from 'react-router-dom'
import AppsListModeEnum = DspBean.AppsListModeEnum
import DeviceTypesEnum = DspBean.DeviceTypesEnum
import DomainsListModeEnum = DspBean.DomainsListModeEnum
import PublisherListModeEnum = DspBean.PublisherListModeEnum

const { Option } = Select

interface DspFormProps {
  mode: FormMode
  auctionType?: DspBean.AuctionTypeEnum
}

const initialEntity: DspBean = {
  name: '',
  revShare: 0,
  auctionType: DspBean.AuctionTypeEnum.Second,
  active: true,
  endpointUrl: '',
  tmax: 1000,
  mediaType: DspBean.MediaTypeEnum.All,
  gzipCompression: true,
  domainsListMode: DomainsListModeEnum.Black,
  appsListMode: AppsListModeEnum.Black,
  publisherListMode: PublisherListModeEnum.Black,
}

export const DspForm: React.FC<DspFormProps> = ({ mode, auctionType = DspBean.AuctionTypeEnum.Second }) => {
  const navigateTo = useNavigate()
  const appContext = useAppContext()

  const [form] = Form.useForm<DspBean>()
  const { id } = useParams<{ id: string }>()
  const idNum = id ? parseInt(id) : 0

  const [entity, setEntity] = useState<DspBean>(initialEntity)
  const [loading, setLoading] = useState<boolean>(false)
  const [showUpload, setShowUpload] = useState<boolean>(false)

  useEffect(() => {
    setLoading(true)
    if (mode === 'edit' && idNum) {
      apiCall(
        setLoading,
        appContext,
        () => dspApi.dsp(idNum),
        (data) => {
          if (data && data.data) {
            setEntity(data.data)
            form.setFieldsValue(data.data)
          }
        },
      )
    } else {
      const defaults: DspBean = {
        ...initialEntity,
        auctionType,
      }
      setEntity(defaults)
      form.setFieldsValue(defaults as any)
      setLoading(false)
    }
  }, [mode, idNum, auctionType])

  const handleSubmit = () => {
    setLoading(true)
    if (mode === FormMode.create) {
      form.validateFields().then((formData) =>
        apiCall(
          setLoading,
          appContext,
          () => dspApi.createDsp({ ...entity, ...formData }),
          () => navigateTo('/dsp'),
        ),
      )
    } else {
      form.validateFields().then((formData) =>
        apiCall(
          setLoading,
          appContext,
          () => dspApi.editDsp({ ...entity, ...formData }, idNum),
          (response) => {
            queueListRepositionOnModifiedChange({
              appContext,
              routeKey: '/dsp',
              previousEntity: entity,
              responseEntity: response?.data
            })

            navigateTo('/dsp')
          },
        ),
      )
    }
  }

  const deleteDsp = () => {
    apiCall(setLoading, appContext, () => dspApi.deleteDsp(idNum), onCancel)
  }

  function onCancel() {
    navigateTo('/dsp')
  }

  return (
    <Spin spinning={loading}>
      <AnchorsForm form={form} onFinish={handleSubmit} onCancel={onCancel} showAnchors>
        <FormCard
          id="general"
          title="General"
          extra={
            <FormItem name="active" valuePropName="checked" initialValue={entity.active} noStyle>
              <StatusSwitch
                entity={{ active: form.getFieldValue('active') }}
                setEntity={(v: any) => {
                  form.setFieldsValue({ active: v.active })
                  setEntity((prev) => ({ ...prev, active: v.active }))
                }}
              />
            </FormItem>
          }
        >
          <FormItem name="name" label="Name" rules={[{ required: true, message: 'Please input DSP name' }]}>
            <Input placeholder="DSP name" />
          </FormItem>

          <FormItem label="RevShare" tooltip="The revenue share percentage taken by the platform from the DSP spend." required>
            <FormItem
              name="revShare"
              noStyle
              initialValue={entity.revShare}
              rules={[{ required: true, message: 'RevShare required' }]}
            >
              <InputNumber min={0} max={99} step={1} addonAfter="%" style={{ width: 140 }} />
            </FormItem>
          </FormItem>

          <FormItem
            name="auctionType"
            label="Auction Type"
            tooltip={
              <>
                <div>Specifies the auction model used - First Price, Second Price, or All.</div>
                <div style={{ marginTop: 8 }}>
                  First Price - In a first-price auction, the winning bidder pays the exact amount they bid. Advertisers pay their full bid price, which encourages strategic bidding.
                </div>
                <div style={{ marginTop: 8 }}>
                  Second Price - In a second-price auction, the winning bidder pays one cent more than the second-highest bid. This model encourages advertisers to bid their true value.
                </div>
                <div style={{ marginTop: 8 }}>
                  All - The system supports both first-price and second-price auctions, automatically handling either type based on the SSP or inventory requirements.
                </div>
              </>
            }
            initialValue={entity.auctionType}
            rules={[{ required: true, message: 'Auction Type required' }]}
          >
            <Radio.Group>
              {[DspBean.AuctionTypeEnum.First, DspBean.AuctionTypeEnum.Second, DspBean.AuctionTypeEnum.All].map(
                (type) => (
                  <Radio key={type} value={type}>
                    {type}
                  </Radio>
                ),
              )}
            </Radio.Group>
          </FormItem>

          <FormItem
            name="gzipCompression"
            label="Gzip Compression"
            tooltip="Enable compression for bid requests to reduce data transfer size."
            valuePropName="checked"
            initialValue={entity.gzipCompression}
          >
            <Switch size="small" />
          </FormItem>

          <FormItem
            name="endpointUrl"
            label="Endpoint URL"
            tooltip="The URL where bid requests are sent to the DSP."
            rules={[{ required: true, message: 'Endpoint URL required' }]}
          >
            <Input />
          </FormItem>

          <FormItem
            name="tmax"
            label="Timeout (ms)"
            tooltip="Maximum time (in milliseconds) to wait for a response from the DSP before timing out."
            rules={[{ required: true, message: 'Timeout required' }]}
            initialValue={entity.tmax}
          >
            <InputNumber min={1} style={{ width: 140 }} />
          </FormItem>

          <FormItem
            name="minBidFloor"
            label="Minimal Bid Floor"
            tooltip="The minimum bid price accepted for impressions from this DSP."
          >
            <InputNumber min={0} step={0.01} style={{ width: 140 }} />
          </FormItem>

          <FormItem name="maxQps" label="QPS" tooltip="Queries per second — limits the number of bid requests sent to the DSP per second.">
            <InputNumber min={1} style={{ width: 140 }} />
          </FormItem>

          <FormItem
            name="mediaType"
            label="Traffic Type"
            tooltip="Specifies whether the DSP receives traffic from websites, apps, or both."
            initialValue={entity.mediaType}
            rules={[{ required: true, message: 'Media Type required' }]}
          >
            <Radio.Group>
              {Object.values(DspBean.MediaTypeEnum).map((val) => (
                <Radio key={val} value={val}>
                  {val}
                </Radio>
              ))}
            </Radio.Group>
          </FormItem>
        </FormCard>

        <FormCard id="connections" title="Connections">
          <FormItem label="Linked SSP" tooltip="Select which supply-side platforms (SSPs) are connected to this DSP." name="sspIds">
            <SspSelector placeholder="Select SSPs" multiple />
          </FormItem>
          <FormItem
            name="bidswitchSspList"
            label="Bidswitch SSP List"
            tooltip="List of SSP identifiers used for Bidswitch integration."
          >
            <Input placeholder="sspA,sspB,sspC" />
          </FormItem>
        </FormCard>

        <FormCard id="targeting" title="Targeting">
          <FormItem name="countriesIds" label="Countries">
            <CountrySelector placeholder="Any Country" />
          </FormItem>

          <FormItem name="deviceTypes" label="Device Types">
            <Select mode="multiple" allowClear placeholder="Select Device Types">
              {Object.values(DeviceTypesEnum).map((dt) => (
                <Option key={dt} value={dt}>
                  {dt}
                </Option>
              ))}
            </Select>
          </FormItem>
        </FormCard>

        <FormCard id="lists" title="Lists">
          <FormItem name="publisherListMode" label="Publisher List Mode">
            <Radio.Group>
              {Object.values(PublisherListModeEnum).map((modeVal) => (
                <Radio key={modeVal} value={modeVal}>
                  {modeVal}
                </Radio>
              ))}
            </Radio.Group>
          </FormItem>

          <FormItem label="Publisher List" name="publisherList">
            <PublisherSelector placeholder="Select Publishers" multiple={true} />
          </FormItem>

          <FormItem name="domainsListMode" label="Domains Mode">
            <Radio.Group>
              {Object.values(DomainsListModeEnum).map((modeVal) => (
                <Radio key={modeVal} value={modeVal}>
                  {modeVal}
                </Radio>
              ))}
            </Radio.Group>
          </FormItem>

          <FormItem name="domainList" label="Domain List">
            <Input placeholder="domain1.com,domain2.com" />
          </FormItem>

          <FormItem name="appsListMode" label="Apps Mode">
            <Radio.Group>
              {Object.values(AppsListModeEnum).map((modeVal) => (
                <Radio key={modeVal} value={modeVal}>
                  {modeVal}
                </Radio>
              ))}
            </Radio.Group>
          </FormItem>

          <FormItem name="appList" label="App List">
            <Input placeholder="appId1,appId2" />
          </FormItem>
        </FormCard>

        <FormCard id="mapping" title="Mapping">
          <FormItem name="mappingVariable" label="Mapping Variable">
            <Input placeholder="Variable name (optional)" />
          </FormItem>

          {mode === 'edit' && idNum && (
            <FormItem label="Mapping Table">
              <Button size="small" onClick={() => setShowUpload((prev) => !prev)}>
                {showUpload ? 'Close' : 'Upload'}
              </Button>
              <AdUnitIds visible={showUpload} toggle={() => setShowUpload((prev) => !prev)} dspId={idNum} />
            </FormItem>
          )}
        </FormCard>

        <ButtonsPanel onCancel={onCancel} showDelete={mode === FormMode.edit} onDelete={deleteDsp} />
      </AnchorsForm>
    </Spin>
  )
}

export default DspForm
